<template>
  <v-container ref="pagePreviewContainer" :fluid="true" class="pa-0">
    <v-row class="preview-shell mx-0 mt-4 mb-0">
      <v-col cols="12" md="7" class="document-preview">
        <v-row ref="scrollContainer" class="preview-image-scroll-container" :style="{ top: documentPreviewImgTop }">
          <v-col cols="12" style="z-index: 1000">
            <div class="viewer-toolbar">
              <v-btn
                variant="text"
                size="small"
                density="comfortable"
                icon="mdi-rotate-right"
                aria-label="Rotate page"
                @click="onRotatePage" />
              <div class="viewer-toolbar__spacer" />
              <v-btn
                variant="text"
                size="small"
                density="comfortable"
                icon="mdi-chevron-left"
                :disabled="currentPageNumber <= 1"
                aria-label="Previous page"
                @click="intakeStore.pageManager.onPrevPage()" />
              <span class="viewer-toolbar__page">{{ currentPageNumber }} / {{ totalPages }}</span>
              <v-btn
                variant="text"
                size="small"
                density="comfortable"
                icon="mdi-chevron-right"
                :disabled="currentPageNumber >= totalPages"
                aria-label="Next page"
                @click="intakeStore.pageManager.onNextPage()" />
            </div>
          </v-col>
          <v-col cols="12" class="preview-image-container">
            <div class="d-flex justify-center align-center" :style="{ transform: `rotate(${pageRotation}deg)` }">
              <div class="image-bounding-box-container" :style="{ position: 'relative', ...transformStyle }">
                <img
                  ref="previewImgRef"
                  class="document-preview-img"
                  :class="{ 'is-zoomed': isZoomed }"
                  :src="intakeStore.pageManager.currentPageRef?.imagePresignedUrl"
                  :style="{
                    opacity: documentPreviewLoaded ? 'unset' : '0%',
                    height: documentPreviewLoaded ? 'unset' : '0px',
                  }"
                  draggable="false"
                  @click="onPreviewClick"
                  @mousedown="onPreviewMouseDown"
                  @load="
                    () => {
                      documentPreviewLoaded = true;
                      nextTick(() => {
                        onWindowScroll();
                      });
                    }
                  " />
                <div
                  v-if="highlightedBoundingBox && highlightedBoundingBox.pageNumber === currentPageNumber"
                  class="highlight-overlay"
                  :style="boundingBoxStyle">
                  <div :style="highlightLabelContainerStyle">
                    <div
                      class="highlight-label"
                      style="
                        position: absolute;
                        top: -35px;
                        left: 0;
                        background: #ff0000;
                        color: white;
                        padding: 2px 4px;
                        border-radius: 2px;
                        font-size: 12px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: 200px;
                      ">
                      {{ highlightedBoundingBox.fieldKey }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <v-skeleton-loader v-if="!documentPreviewLoaded" type="image@4" />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        md="5"
        class="document-sidebar"
        :class="{ 'document-sidebar--readonly': isReadonly, 'lh-readonly-fields': isReadonly }">
        <v-row>
          <v-col style="height: 52px" cols="12" align="right">
            <!-- Alignment row -->
            <v-menu>
              <template #activator="{ props: activatorProps }">
                <v-btn
                  variant="tonal"
                  size="small"
                  rounded
                  class="snooze-btn"
                  :color="snooze.isSnoozed ? 'warning' : 'primary'"
                  v-bind="activatorProps">
                  <v-icon size="16" start>mdi-alarm</v-icon>
                  {{ snooze.isSnoozed ? "Snoozed" : "Snooze" }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item v-if="snooze.isSnoozed" @click="onSnooze(0)">
                  <v-list-item-title>
                    Snoozed until
                    <br />
                    <v-chip size="small" color="success" variant="tonal">
                      {{ snooze.snoozeUntil.toLocaleString() }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="pt-2"> Click to remove </v-list-item-subtitle>
                </v-list-item>
                <v-list-item v-for="choice in snoozeChoices" :key="choice.value" @click="onSnooze(choice.value)">
                  <v-list-item-title>{{ choice.label }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item v-if="mentionTarget" @click="showMentionDialog = true">
                  <v-list-item-title>
                    <v-icon size="small" class="mr-2">mdi-account-plus-outline</v-icon>
                    Tag Teammate
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="onArchive">
                  <v-list-item-title class="text-error">
                    <v-icon color="error" size="small" class="mr-2">mdi-archive</v-icon>
                    {{
                      (intakeStore.intakeDocument ?? intakeStore.intakeOrder)?.status === DOCUMENT_STAGE.ARCHIVED
                        ? "Unarchive"
                        : "Archive"
                    }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <MentionTeammateDialog v-if="mentionTarget" v-model="showMentionDialog" :target="mentionTarget" />
          </v-col>
        </v-row>
        <v-row>
          <slot name="before" />
        </v-row>
        <v-row>
          <v-col cols="12">
            <template v-if="props.patient && patientFields && filteredPatientFormGroups.length">
              <template
                v-for="(group, pix) in filteredPatientFormGroups"
                :key="`patient-field-${props.patient.id}-${group}`">
                <v-card
                  color="surface"
                  class="intake-card mb-4"
                  :class="{ 'intake-card--invalid': !fieldFormPatientRefs[pix]?.formRef?.isValid }">
                  <IntakeCardTitle :title="group" :invalid="!fieldFormPatientRefs[pix]?.formRef?.isValid" />
                  <v-card-text>
                    <FieldsForm
                      v-if="patientFields"
                      ref="fieldFormPatientRefs"
                      :key="`pf-${props.patient.id}-${group}`"
                      :read-only="readOnlyFields"
                      :fields="patientFields"
                      :data="props.patient"
                      :group="group"
                      @highlight-bounding-box="onHighlightBoundingBox"
                      @attribute-edit="(e, field) => onAttributeChange(e, field, 'PATIENT', props.patient.id)" />
                  </v-card-text>
                </v-card>
              </template>
            </template>
            <template v-if="props.orderForm && orderFormFields && shouldDisplayOrderForm">
              <v-btn
                v-if="!readOnlyFields && intakeStore.stage !== INTAKE_STAGE.QUALIFY_PATIENT"
                class="mb-3"
                color="error"
                variant="text"
                size="small"
                prepend-icon="mdi-trash-can-outline"
                @click="onDeleteContent(props.orderForm, 'ORDER_FORM')">
                Delete this order form
              </v-btn>
              <template v-for="(group, ix) in filteredOrderFormGroups" :key="group">
                <v-card
                  color="surface"
                  class="intake-card mb-4"
                  :class="{ 'intake-card--invalid': !fieldFormOrderRefs[ix]?.formRef.isValid }">
                  <IntakeCardTitle :title="group" :invalid="!fieldFormOrderRefs[ix]?.formRef.isValid" />
                  <v-card-text :class="{ invisible: !isOrderFormGroupVisible(props.orderForm, group) }">
                    <div v-if="!readOnlyFields" class="intake-card__actions">
                      <v-btn
                        variant="text"
                        size="small"
                        color="error"
                        prepend-icon="mdi-trash-can-outline"
                        @click="onDeleteOrderFormGroup(props.orderForm, group)">
                        Remove
                      </v-btn>
                      <v-btn
                        v-if="group !== 'Emergency Contacts'"
                        variant="text"
                        size="small"
                        prepend-icon="mdi-eraser"
                        @click="onClearContent(props.orderForm, 'ORDER_FORM', group)">
                        Clear all data
                      </v-btn>
                    </div>
                    <v-col>
                      <FieldsForm
                        v-if="orderFormFields"
                        ref="fieldFormOrderRefs"
                        :read-only="readOnlyFields"
                        :fields="orderFormFields"
                        :data="orderForm"
                        :group="group"
                        @highlight-bounding-box="(data) => onHighlightBoundingBox(data, props.orderForm)"
                        @attribute-edit="(e, field) => onAttributeChange(e, field, 'ORDER_FORM', props.orderForm.id)" />
                    </v-col>
                  </v-card-text>
                  <v-card-text v-if="!isOrderFormGroupVisible(props.orderForm, group) && !readOnlyFields">
                    <v-btn
                      variant="tonal"
                      size="small"
                      color="primary"
                      prepend-icon="mdi-plus"
                      @click="onAddOrderFormGroup(props.orderForm, group)">
                      Add {{ group }}
                    </v-btn>
                  </v-card-text>
                </v-card>
              </template>
            </template>
            <template v-if="shouldDisplayInsuranceCard">
              <template v-for="(fields, ix) in insuranceCardFields" :key="`ins-${ix}`">
                <v-card
                  class="intake-card mb-4"
                  :class="{
                    'intake-card--invalid': !fieldFormInsuranceRefs[ix]?.formRef.isValid,
                    'intake-card--selected':
                      intakeStore.stage === INTAKE_STAGE.QUALIFY_PATIENT && isCardSelected(props.insuranceCards[ix].id),
                  }"
                  color="surface">
                  <div class="intake-card__title intake-card__title--stacked">
                    <IntakeCardTitle
                      variant="row"
                      title="Insurance Card"
                      :invalid="!fieldFormInsuranceRefs[ix]?.formRef.isValid">
                      <v-chip
                        v-if="props.insuranceCards[ix].cardRole === 'PRIMARY'"
                        color="blue"
                        size="small"
                        rounded="lg"
                        variant="tonal">
                        Primary
                      </v-chip>
                    </IntakeCardTitle>
                    <div class="intake-card__subtitle">
                      {{ props.insuranceCards[ix].payorName }}
                      <span v-if="props.insuranceCards[ix].insurancePlan">
                        — {{ props.insuranceCards[ix].insurancePlan }}
                      </span>
                    </div>
                    <div v-if="props.insuranceCards[ix].createdOn" class="intake-card__meta">
                      Added on {{ props.insuranceCards[ix].createdOn?.toLocaleDateString() }}
                    </div>
                    <div v-if="intakeStore.stage === INTAKE_STAGE.QUALIFY_PATIENT" class="mt-2">
                      <v-btn
                        v-if="!readOnlyFields"
                        size="small"
                        rounded
                        :variant="isCardSelected(props.insuranceCards[ix].id) ? 'flat' : 'outlined'"
                        :color="isCardSelected(props.insuranceCards[ix].id) ? 'success' : 'primary'"
                        :prepend-icon="isCardSelected(props.insuranceCards[ix].id) ? 'mdi-check' : undefined"
                        @click.capture="onMarkAsActiveInsurance(props.insuranceCards[ix])">
                        {{ isCardSelected(props.insuranceCards[ix].id) ? "Selected" : "Select Card" }}
                      </v-btn>
                    </div>
                  </div>
                  <v-card-text>
                    <div v-if="!readOnlyFields" class="intake-card__actions">
                      <v-btn
                        v-if="canOcrCreateInsuranceCard"
                        variant="text"
                        size="small"
                        color="error"
                        prepend-icon="mdi-trash-can-outline"
                        @click="onDeleteContent(props.insuranceCards[ix], 'INSURANCE_CARD')">
                        Delete card
                      </v-btn>
                      <v-btn
                        variant="text"
                        size="small"
                        prepend-icon="mdi-eraser"
                        @click="onClearContent(props.insuranceCards[ix], 'INSURANCE_CARD')">
                        Clear all data
                      </v-btn>
                    </div>
                    <FieldsForm
                      ref="fieldFormInsuranceRefs"
                      :read-only="readOnlyFields"
                      :fields="fields"
                      :data="props.insuranceCards[ix]"
                      @highlight-bounding-box="(data) => onHighlightBoundingBox(data, props.insuranceCards[ix])"
                      @attribute-edit="
                        (e, field) => onAttributeChange(e, field, 'INSURANCE_CARD', props.insuranceCards[ix].id)
                      " />
                  </v-card-text>
                </v-card>
              </template>
            </template>
            <template v-if="shouldDisplayToDo">
              <v-card
                v-for="(fields, ix) in toDoFields"
                :key="ix"
                class="intake-card mb-4"
                :class="{ 'intake-card--invalid': !fieldFormToDoRefs[ix]?.formRef.isValid }"
                color="surface">
                <IntakeCardTitle title="To Do" :invalid="!fieldFormToDoRefs[ix]?.formRef.isValid" />
                <v-card-text>
                  <div v-if="!readOnlyFields" class="intake-card__actions">
                    <v-btn
                      variant="text"
                      size="small"
                      color="error"
                      prepend-icon="mdi-trash-can-outline"
                      @click="onDeleteContent(props.toDos[ix], 'TO_DO')">
                      Delete to do
                    </v-btn>
                    <v-btn
                      variant="text"
                      size="small"
                      prepend-icon="mdi-eraser"
                      @click="onClearContent(props.toDos[ix], 'TO_DO')">
                      Clear all data
                    </v-btn>
                  </div>
                  <FieldsForm
                    ref="fieldFormToDoRefs"
                    :read-only="readOnlyFields"
                    :fields="fields"
                    :data="props.toDos[ix]"
                    @attribute-edit="(e, field) => onAttributeChange(e, field, 'TO_DO', props.toDos[ix].id)" />
                </v-card-text>
              </v-card>
            </template>
          </v-col>
          <v-col v-if="intakeStore.stage === INTAKE_STAGE.INTAKE" cols="12">
            <div v-if="canAddOrderForm || canAddInsuranceCard || canAddToDo" class="intake-add-content">
              <div v-if="canAddOrderForm || canAddInsuranceCard" class="intake-add-content__hint">
                Autopopulate from this page by adding one of the following
              </div>
              <div class="intake-add-content__actions">
                <v-btn
                  v-if="canAddInsuranceCard"
                  variant="outlined"
                  color="primary"
                  rounded
                  prepend-icon="mdi-card-account-details-outline"
                  @click="onAddInsuranceCard">
                  New Insurance Card
                </v-btn>
                <v-btn
                  v-if="canAddOrderForm"
                  variant="outlined"
                  color="primary"
                  rounded
                  prepend-icon="mdi-file-document-plus-outline"
                  @click="onAddOrderForm">
                  New Order Form
                </v-btn>
                <v-btn
                  v-if="canAddToDo"
                  variant="outlined"
                  color="primary"
                  rounded
                  prepend-icon="mdi-checkbox-marked-circle-plus-outline"
                  @click="onAddToDo">
                  New To Do
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <slot name="after" />
        </v-row>
      </v-col>
    </v-row>
    <!-- Generic Confirmation Dialog -->
    <v-dialog v-model="confirmDialog.show" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ confirmDialog.title }}</v-card-title>
        <v-card-text>{{ confirmDialog.text }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="resolveConfirmDialog(false)">
            {{ confirmDialog.cancelLabel || "Cancel" }}
          </v-btn>
          <v-btn color="primary" variant="flat" @click="resolveConfirmDialog(true)">
            {{ confirmDialog.confirmLabel || "Confirm" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { DOCUMENT_CLASSIFICATIONS, INTAKE_STAGE, IntakeOrderForm, DOCUMENT_STAGE } from "@/models/intake";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useIntakeStore } from "@/stores/intakeStore";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useConfirmDialogStore } from "@/stores";
import FieldsForm from "../FieldsForm.vue";
import IntakeCardTitle from "./IntakeCardTitle.vue";
import MentionTeammateDialog from "@/components/dashboard/MentionTeammateDialog.vue";
import { getClientIntakeUI } from "@/clientSpecific/base/intakeUI";
import { useResponsive } from "@/composables/useResponsive";

const { isReadOnlyDevice } = useResponsive();

const emit = defineEmits(["attributeEdit", "addContent", "deleteContent", "snooze", "selectInsuranceCard", "archive"]);
const props = defineProps({
  orderForm: {
    required: false,
    type: IntakeOrderForm,
    default: null,
  },
  insuranceCards: {
    required: false,
    type: Array,
    default: () => [],
  },
  toDos: {
    required: false,
    type: Array,
    default: () => [],
  },
  tags: {
    required: false,
    type: Array,
    default: () => [],
  },
  errors: {
    required: false,
    type: Object,
    default: () => ({}),
  },
  activeInsuranceCardId: {
    required: false,
    type: Number,
    default: null,
  },
  patient: {
    required: false,
    type: Object,
    default: null,
  },
});

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();
const confirmDialogStore = useConfirmDialogStore();

const showMentionDialog = ref(false);
const mentionTarget = computed(() => {
  if (intakeStore.intakeDocument?.key) return { kind: "intake_document", key: intakeStore.intakeDocument.key };
  if (intakeStore.intakeOrder?.id) return { kind: "intake_order", key: intakeStore.intakeOrder.id };
  return null;
});

// Sidebar is read-only on view-only handheld devices; a narrow desktop window stays editable.
const isReadonly = isReadOnlyDevice;

const page = computed(() => intakeStore.pageManager.currentPageRef);
const currentPageNumber = computed(() => intakeStore.pageManager.currentPageIndex + 1);
const totalPages = computed(() => intakeStore.pageManager.pages.length);

const fieldFormOrderRefs = ref([]);
const fieldFormInsuranceRefs = ref([]);
const fieldFormToDoRefs = ref([]);
const fieldFormPatientRefs = ref([]);

const documentPreviewLoaded = ref(false);
const previewImgRef = ref(null);
const zoomStyle = ref({});
const isDragging = ref(false);
const maxCardsPerPage = 5;
const orderFormPanels = ref([]);
const scrollContainer = ref(null);
const documentPreviewImgTop = ref(0);
const pagePreviewContainer = ref(null);
const highlightedBoundingBox = ref(null);
const highlightTimer = ref(null);
const pageRotation = computed(() => page.value?.rotationDegrees ?? 0);
const resizeObserver = ref(null);
const intakeUI = ref(null);

const confirmDialog = ref({ show: false, title: "", text: "", confirmLabel: "", cancelLabel: "" });
let confirmDialogResolver = null;

const showConfirmDialog = ({ title, text, confirmLabel, cancelLabel }) => {
  return new Promise((resolve) => {
    confirmDialogResolver = resolve;
    confirmDialog.value = {
      show: true,
      title,
      text,
      confirmLabel: confirmLabel || "Confirm",
      cancelLabel: cancelLabel || "Cancel",
    };
  });
};

const resolveConfirmDialog = (result) => {
  confirmDialog.value.show = false;
  if (confirmDialogResolver) {
    confirmDialogResolver(result);
    confirmDialogResolver = null;
  }
};

// Track document classifications to trigger reactivity when they change
const documentClassifications = computed(() => {
  return intakeStore.pageManager?.pages?.map((p) => p.documentType) ?? [];
});

// Determines if the order form should be displayed based on current classifications
const shouldDisplayOrderForm = computed(() => {
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  if (!intakeUI.value?.shouldShowOrderForm) {
    return true; // Default behavior if method doesn't exist
  }
  return intakeUI.value.shouldShowOrderForm();
});

// Determines if insurance cards should be displayed based on current classifications
const shouldDisplayInsuranceCard = computed(() => {
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  if (!intakeUI.value?.shouldShowInsuranceCard) {
    return true; // Default behavior if method doesn't exist
  }
  return intakeUI.value.shouldShowInsuranceCard();
});

// Determines if To-Do cards should be displayed based on current classifications
const shouldDisplayToDo = computed(() => {
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  if (!intakeUI.value?.shouldShowToDo) {
    return true; // Default behavior if method doesn't exist
  }
  return intakeUI.value.shouldShowToDo();
});

const orderFormFields = computed(() => {
  // Include documentClassifications in the dependency to re-compute when classifications change
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  return intakeUI.value?.getOrderFormFields(props.orderForm) ?? null;
});
const insuranceCardFields = computed(() =>
  intakeUI.value ? (props.insuranceCards ?? []).map((card) => intakeUI.value?.getInsuranceCardFields(card)) : []
);
const toDoFields = computed(() => {
  // Include documentClassifications in the dependency to re-compute when classifications change
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  const allToDos = props.toDos ?? [];
  return intakeUI.value ? allToDos.map((toDo) => intakeUI.value?.getToDosFields(toDo, allToDos)) : [];
});
const filteredOrderFormGroups = computed(() => {
  if (!orderFormFields.value?.fields) return [];

  const groups = [...new Set(orderFormFields.value.fields.map((field) => field.group).filter(Boolean))];
  return groups.filter((group) => {
    const fieldsForGroup = orderFormFields.value.forGroup(group);
    return fieldsForGroup && fieldsForGroup.length > 0;
  });
});

const patientFields = computed(() => {
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  if (!props.patient || !intakeUI.value) {
    return null;
  }
  return intakeUI.value.getPatientFields(props.patient, intakeStore.intakeDocument?.patientGuess);
});

const filteredPatientFormGroups = computed(() => {
  if (!patientFields.value?.fields?.length) return [];

  const groups = [...new Set(patientFields.value.fields.map((field) => field.group).filter(Boolean))];
  return groups.filter((group) => {
    const fieldsForGroup = patientFields.value.forGroup(group);
    return fieldsForGroup && fieldsForGroup.length > 0;
  });
});

const canOcrCreateInsuranceCard = computed(() => {
  return requirementsStore.clientConfig?.intake?.ocrCreatesInsuranceCard ?? true;
});
const readOnlyFields = computed(() => intakeStore.readOnly);

const formatError = (error, formRef) => {
  const formatted = { ...error };
  const item = formRef.items.find((item) => item.id === error.id);
  formatted.label = item?.vm.props.label ?? "";
  formatted.item = item;
  return formatted;
};

/**
 * Returns an array of errors with the following structure:
 * {
 *   label: string,
 *   item: VuetifyItem,
 *   error: string,
 * }
 */
const formErrors = computed(() => {
  let errors = [];
  fieldFormOrderRefs.value.forEach((ref) => {
    if (!ref.formRef.isValid) {
      errors = errors.concat(ref.formRef.errors.map((e) => formatError(e, ref.formRef)));
    }
  });
  fieldFormInsuranceRefs.value.forEach((ref) => {
    if (!ref.formRef.isValid) {
      errors = errors.concat(ref.formRef.errors.map((e) => formatError(e, ref.formRef)));
    }
  });
  fieldFormToDoRefs.value.forEach((ref) => {
    if (!ref.formRef.isValid) {
      errors = errors.concat(ref.formRef.errors.map((e) => formatError(e, ref.formRef)));
    }
  });
  fieldFormPatientRefs.value.forEach((ref) => {
    if (ref?.formRef && !ref.formRef.isValid) {
      errors = errors.concat(ref.formRef.errors.map((e) => formatError(e, ref.formRef)));
    }
  });
  return errors;
});

const highlightLabelContainerStyle = computed(() => ({
  position: "absolute",
  top: "0px",
  left: "0px",
  transform: `rotate(${-pageRotation.value}deg)`,
  transformOrigin: "top left",
}));

const boundingBoxStyle = computed(() => {
  if (!highlightedBoundingBox.value || !documentPreviewLoaded.value) {
    return {};
  }

  const imgElement = document.querySelector(".document-preview-img");
  const containerElement = document.querySelector(".image-bounding-box-container");
  if (!imgElement || !containerElement) {
    return {};
  }

  // Get the original bounding box coordinates (0-1 range)
  const [x1, y1, x2, y2] = highlightedBoundingBox.value.boundingBox;

  // Get the actual displayed image dimensions
  const displayWidth = imgElement.offsetWidth;
  const displayHeight = imgElement.offsetHeight;

  // Calculate the position and size relative to the image
  const left = x1 * displayWidth;
  const top = y1 * displayHeight;
  const width = (x2 - x1) * displayWidth;
  const height = (y2 - y1) * displayHeight;

  // Enlarge the box to forgive coordinate imprecision. Callers may pass a gentler factor
  // (e.g. policy-review passage boxes, which need less inflation than intake's single-line fields);
  // defaults to 1.5 for the intake-OCR flow.
  const scaleFactor = highlightedBoundingBox.value.scaleFactor ?? 1.5;
  const scaledWidth = width * scaleFactor;
  // Vertical points are off more often than horizontal, so scaling is increased
  const scaledHeight = height * scaleFactor * scaleFactor;

  // Center the scaled bounding box within the original area
  const scaledLeft = left + (width - scaledWidth) / 2;
  const scaledTop = top + (height - scaledHeight) / 2;

  return {
    position: "absolute",
    left: `${scaledLeft}px`,
    top: `${scaledTop}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    backgroundColor: "rgba(255, 0, 0, 0.35)",
    pointerEvents: "none",
    transition: "all 0.3s ease",
    zIndex: 1000,
    borderRadius: "4px",
    boxShadow: "0 0 10px 15px rgba(255, 0, 0, 0.35)",
  };
});

const transformStyle = computed(() => {
  const style = {
    transform: `${zoomStyle.value.transform ?? ""}`,
    "transform-origin": `${zoomStyle.value["transform-origin"] ?? ""}`,
    transition: isDragging.value ? "none" : "all 0.3s ease",
  };
  if (pageRotation.value % 180 !== 0) {
    // If the image is sideways, the max height should be the parent divs width
    const imgEle = document.querySelector(".document-preview-img");
    if (imgEle && imgEle.parentElement) {
      const { width } = imgEle.parentElement.getBoundingClientRect();
      style.maxHeight = `${width}px`;
    }
  }
  return style;
});

const onRotatePage = () => {
  if (page.value) {
    page.value.rotationDegrees = (pageRotation.value + 90) % 360;
    page.value.debouncedSaveUpdates();
  }
};

// metadata.pageNumber is relative to the source document; the page manager list may be
// reordered (clinical review) or partial (qualify patient), so resolve by page identity.
const resolveHighlightPageNumber = (data, sourceForm) => {
  const documentKey = sourceForm?.pages?.[0]?.documentKey;
  if (!documentKey) {
    return data.pageNumber;
  }
  const pageIx = intakeStore.pageManager.pages.findIndex(
    (p) => p.documentKey === documentKey && p.pageNumber === data.pageNumber
  );
  return pageIx === -1 ? data.pageNumber : pageIx + 1;
};

const onHighlightBoundingBox = (data, sourceForm = null) => {
  if (!data) {
    return;
  }
  data = { ...data, pageNumber: resolveHighlightPageNumber(data, sourceForm) };

  // Clear any existing highlight and timer
  highlightedBoundingBox.value = null;
  if (highlightTimer.value) {
    clearTimeout(highlightTimer.value);
  }

  // Switch to the correct page if needed
  if (data.pageNumber !== currentPageNumber.value) {
    const pageDiff = data.pageNumber - currentPageNumber.value;
    if (pageDiff > 0) {
      for (let i = 0; i < pageDiff; i++) {
        intakeStore.pageManager.onNextPage();
      }
    } else {
      for (let i = 0; i < -pageDiff; i++) {
        intakeStore.pageManager.onPrevPage();
      }
    }
    // Wait for page switch and image load before showing highlight
    setTimeout(() => {
      highlightedBoundingBox.value = data;
      // Set new timer to clear highlight
      highlightTimer.value = setTimeout(() => {
        if (highlightedBoundingBox.value) {
          highlightedBoundingBox.value = null;
          highlightTimer.value = null;
        }
      }, 7000);
    }, 2000); // Wait 2 seconds for page switch and image load
  } else {
    // If we're already on the correct page, show highlight immediately
    highlightedBoundingBox.value = data;
    // Set new timer to clear highlight
    highlightTimer.value = setTimeout(() => {
      if (highlightedBoundingBox.value) {
        highlightedBoundingBox.value = null;
        highlightTimer.value = null;
      }
    }, 7000);
  }
};

// Add a watch to handle page changes
watch(currentPageNumber, () => {
  // Reset zoom and clear highlight when page changes
  zoomStyle.value = {};
  if (highlightedBoundingBox.value) {
    highlightedBoundingBox.value = null;
  }
});

// Add a watch to handle zoom changes
watch(
  zoomStyle,
  () => {
    // Force recalculation of bounding box when zoom changes
    if (highlightedBoundingBox.value) {
      // Trigger reactivity by temporarily clearing and restoring
      const currentBox = highlightedBoundingBox.value;
      highlightedBoundingBox.value = null;
      nextTick(() => {
        highlightedBoundingBox.value = currentBox;
      });
    }
  },
  { deep: true }
);

// Add a watch to handle image load
watch(documentPreviewLoaded, (loaded) => {
  // Force recalculation of bounding box when image loads
  if (loaded && highlightedBoundingBox.value) {
    // Trigger reactivity by temporarily clearing and restoring
    const currentBox = highlightedBoundingBox.value;
    highlightedBoundingBox.value = null;
    nextTick(() => {
      highlightedBoundingBox.value = currentBox;
    });
  }
});

// Add a watcher for image resize/zoom changes
watch(
  transformStyle,
  () => {
    // Force recalculation of bounding box when image transforms change
    if (highlightedBoundingBox.value) {
      // Trigger reactivity by temporarily clearing and restoring
      const currentBox = highlightedBoundingBox.value;
      highlightedBoundingBox.value = null;
      nextTick(() => {
        highlightedBoundingBox.value = currentBox;
      });
    }
  },
  { deep: true }
);

defineExpose({
  orderFormPanels,
  scrollContainer,
  pagePreviewContainer,
  onHighlightBoundingBox,
  formErrors,
});

const snoozeChoices = ref([
  {
    label: "15 minutes",
    value: 15,
  },
  {
    label: "1 hour",
    value: 60,
  },
  {
    label: "6 hours",
    value: 6 * 60,
  },
  {
    label: "1 day",
    value: 24 * 60,
  },
  {
    label: "1 week",
    value: 7 * 24 * 60,
  },
]);

const onWindowScroll = () => {
  /**
   * Layout architecture for static scrolling in the container
   * <PagePreview Container>
   *   <DocumentPreviewImgContainer> <-- Relative position, overflow visible, does not move
   *     <DocumentPreviewImg> <-- Relative position, moves via "top" and the parents overflow keeps it visible
   *   </DocumentPreviewImgContainer>
   * </PagePreviewContainer> <-- Image bottom must not cross the container bottom
   */
  if (scrollContainer.value && pagePreviewContainer.value) {
    // bottom of the container
    const { bottom: stopBottom } = pagePreviewContainer.value.$el.getBoundingClientRect();
    // top of the image parent, which is stationary so we get the relative position of the scroll
    const { top: parentTop } = scrollContainer.value.$el.parentElement.getBoundingClientRect();
    // height of the image, important to know where to stop the scroll
    const { height: imgHeight } = scrollContainer.value.$el.getBoundingClientRect();

    if (parentTop < 0) {
      // Parent has scrolled above the viewport, so we need to scroll the image up
      if (imgHeight < stopBottom - 12) {
        // If the image bottom does not cross the container bottom, we can just scroll the image with the screen
        documentPreviewImgTop.value = `${parentTop * -1}px`;
      } else {
        // If the image bottom crosses the container bottom, we need to scroll the image up to the bottom of the viewport
        documentPreviewImgTop.value = `${stopBottom - parentTop - (imgHeight + 12)}px`;
      }
    } else {
      documentPreviewImgTop.value = 0;
    }
  }
};

onMounted(async () => {
  window.addEventListener("scroll", onWindowScroll);

  // Set up ResizeObserver to watch for image size changes
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver.value = new ResizeObserver(() => {
      // Force recalculation of bounding box when image size changes
      if (highlightedBoundingBox.value) {
        const currentBox = highlightedBoundingBox.value;
        highlightedBoundingBox.value = null;
        nextTick(() => {
          highlightedBoundingBox.value = currentBox;
        });
      }
    });

    // Start observing the image element when it's available
    const observeImage = () => {
      const imgElement = document.querySelector(".document-preview-img");
      if (imgElement) {
        resizeObserver.value.observe(imgElement);
      } else {
        // If image not ready, try again in a bit
        setTimeout(observeImage, 100);
      }
    };

    observeImage();
  }

  intakeUI.value = await getClientIntakeUI(
    requirementsStore.clientConfig,
    {
      onHighlightBoundingBox: (data) => onHighlightBoundingBox(data, props.orderForm),
      onAttributeChange,
      revalidateOrderForm,
      revalidateInsuranceCards,
      addContentWithData,
      showConfirmDialog,
    },
    intakeStore.stage,
    intakeStore.pageManager
  );
});

onUnmounted(() => {
  window.removeEventListener("scroll", onWindowScroll);
  document.removeEventListener("mousemove", onPreviewMouseMove);
  document.removeEventListener("mouseup", onPreviewMouseUp);
  if (highlightTimer.value) {
    clearTimeout(highlightTimer.value);
  }
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }
});

// Watch for page classification changes and re-validate ToDo forms
watch(
  documentClassifications,
  () => {
    // Trigger validation on all ToDo forms when page classifications change
    nextTick(() => {
      fieldFormToDoRefs.value.forEach((ref) => {
        ref?.formRef?.validate?.();
      });
    });
  },
  { deep: true }
);

const snooze = computed(() => {
  const object = intakeStore.intakeDocument ?? intakeStore.intakeOrder;
  return {
    isSnoozed: object?.isSnoozed,
    snoozeUntil: object?.snoozeUntil,
  };
});

const canAddInsuranceCard = computed(() => {
  // Include documentClassifications in the dependency to re-compute when classifications change
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  if (!canOcrCreateInsuranceCard.value || readOnlyFields.value) {
    return false;
  }
  // Check if intakeUI has a shouldShowInsuranceCard method and use it
  if (intakeUI.value?.shouldShowInsuranceCard && !intakeUI.value.shouldShowInsuranceCard()) {
    return false;
  }
  const cardCount = props.insuranceCards.reduce(
    (acc, card) => (card?.pages[0]?.pageNumber === currentPageNumber.value ? acc + 1 : acc),
    0
  );
  return cardCount < maxCardsPerPage;
});

const canAddOrderForm = computed(() => {
  // Include documentClassifications in the dependency to re-compute when classifications change
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  if (props.orderForm || readOnlyFields.value) return false;
  // Check if intakeUI has a shouldShowOrderForm method and use it
  if (intakeUI.value?.shouldShowOrderForm) {
    return intakeUI.value.shouldShowOrderForm();
  }
  return true;
});

const canAddToDo = computed(() => {
  // Include documentClassifications in the dependency to re-compute when classifications change
  // eslint-disable-next-line no-unused-vars
  const _classifications = documentClassifications.value;
  if (!intakeUI.value || readOnlyFields.value) return false;
  // Check if intakeUI has a shouldShowToDo method and use it
  if (intakeUI.value?.shouldShowToDo) {
    if (!intakeUI.value.shouldShowToDo()) return false;
    // Check if max ToDos limit has been reached
    if (intakeUI.value.maxToDos && props.toDos.length >= intakeUI.value.maxToDos()) {
      return false;
    }
    return true;
  }
  try {
    const sampleToDoFields = intakeUI.value.getToDosFields({});
    return sampleToDoFields?.fields?.length > 0;
  } catch (error) {
    return false;
  }
});

watch(
  () => page.value?.imagePresignedUrl,
  () => {
    documentPreviewLoaded.value = false;

    // Start observing the new image when URL changes
    if (resizeObserver.value) {
      const observeNewImage = () => {
        const imgElement = document.querySelector(".document-preview-img");
        if (imgElement) {
          resizeObserver.value.observe(imgElement);
        } else {
          setTimeout(observeNewImage, 100);
        }
      };
      observeNewImage();
    }
  }
);

const isCardSelected = (cardId) => {
  return props.activeInsuranceCardId === cardId;
};

const onSnooze = (value) => {
  emit("snooze", { minutes: value });
};

const onMarkAsActiveInsurance = (card) => {
  if (!isCardSelected(card.id)) {
    emit("selectInsuranceCard", card.id);
  }
};

const isZoomed = computed(() => !!zoomStyle.value.transform);
const dragStartPos = ref({ x: 0, y: 0 });
const dragStartOrigin = ref({ x: 0, y: 0 });
const DRAG_THRESHOLD = 5; // pixels of movement before considered a drag
const zoomFactor = 2;

const getRotatedRatios = (rawX, rawY, width, height) => {
  switch (pageRotation.value) {
    case 90:
      return { hRatio: rawY / height, vRatio: 1 - rawX / width };
    case 180:
      return { hRatio: 1 - rawX / width, vRatio: 1 - rawY / height };
    case 270:
      return { hRatio: 1 - rawY / height, vRatio: rawX / width };
    default:
      return { hRatio: rawX / width, vRatio: rawY / height };
  }
};

const onPreviewMouseDown = (ev) => {
  if (!isZoomed.value) return;
  ev.preventDefault();
  isDragging.value = false;
  dragStartPos.value = { x: ev.clientX, y: ev.clientY };
  dragStartOrigin.value = { ...zoomStyle.value.origin };
  document.addEventListener("mousemove", onPreviewMouseMove);
  document.addEventListener("mouseup", onPreviewMouseUp);
};

const onPreviewMouseMove = (ev) => {
  const dx = ev.clientX - dragStartPos.value.x;
  const dy = ev.clientY - dragStartPos.value.y;

  if (!isDragging.value && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
    return;
  }
  isDragging.value = true;

  if (!previewImgRef.value) return;
  const bounds = previewImgRef.value.getBoundingClientRect();

  // Convert pixel drag distance to percentage of the image's natural (unzoomed) size
  // Moving the mouse right should shift the view right, which means decreasing transform-origin x
  const originX = dragStartOrigin.value.x - (dx / bounds.width) * zoomFactor * 100;
  const originY = dragStartOrigin.value.y - (dy / bounds.height) * zoomFactor * 100;

  // Clamp to 0-100% so we don't pan beyond the image edges
  const clampedX = Math.max(0, Math.min(100, originX));
  const clampedY = Math.max(0, Math.min(100, originY));

  zoomStyle.value = {
    transform: `scale(${zoomFactor})`,
    "transform-origin": `${clampedX}% ${clampedY}%`,
    origin: { x: clampedX, y: clampedY },
  };
};

const onPreviewMouseUp = () => {
  document.removeEventListener("mousemove", onPreviewMouseMove);
  document.removeEventListener("mouseup", onPreviewMouseUp);
};

const onPreviewClick = (ev) => {
  // If the user was dragging, don't toggle zoom
  if (isDragging.value) {
    isDragging.value = false;
    return;
  }

  if (isZoomed.value) {
    zoomStyle.value = {};
    return;
  }

  const bounds = ev.target.getBoundingClientRect();
  const rawX = ev.clientX - bounds.left;
  const rawY = ev.clientY - bounds.top;
  const { hRatio, vRatio } = getRotatedRatios(rawX, rawY, bounds.width, bounds.height);

  zoomStyle.value = {
    transform: `scale(${zoomFactor})`,
    "transform-origin": `${hRatio * 100}% ${vRatio * 100}%`,
    origin: { x: hRatio * 100, y: vRatio * 100 },
  };
};

const onAttributeChange = (e, field, type, id) => {
  let value = e?.currentTarget ? e.currentTarget.value : e;
  let data = null;
  if (field.isExtraData) {
    if (type === "ORDER_FORM") {
      data = props.orderForm;
    } else if (type === "INSURANCE_CARD") {
      const ix = props.insuranceCards.findIndex((card) => card.id === id);
      data = props.insuranceCards[ix];
    } else if (type === "PATIENT") {
      data = props.patient;
    }
    if (!data) {
      return;
    }
    value = { ...(data.extraData ?? {}), [field.key]: value };
    field = { ...field, key: "extraData" };
  }
  emit("attributeEdit", field, value, type, id);
};

/**
 * Revalidates all order form fields.
 * This is useful when programmatically updating related fields (e.g., provider fields)
 * where validation needs to be re-run after all fields are updated.
 */
const revalidateOrderForm = () => {
  fieldFormOrderRefs.value.forEach((ref) => {
    ref.formRef?.validate();
  });
};

const revalidateInsuranceCards = () => {
  fieldFormInsuranceRefs.value.forEach((ref) => {
    ref.formRef?.validate();
  });
};

const addContentWithData = (documentType, initialData) => {
  emit("addContent", page.value, documentType, initialData);
};

const onAddInsuranceCard = () => {
  emit("addContent", page.value, DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD);
};

const onAddOrderForm = () => {
  emit("addContent", page.value, DOCUMENT_CLASSIFICATIONS.ORDER_FORM);
};

const onAddToDo = () => {
  emit("addContent", page.value, DOCUMENT_CLASSIFICATIONS.TO_DO);
};

const onDeleteContent = (content, documentType) => {
  emit("deleteContent", content, documentType);
};

const onClearContent = async (item, documentType, formGroup) => {
  const contentType = formGroup
    ? formGroup
    : documentType === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD
    ? "insurance card"
    : "order form";
  const confirmed = await confirmDialogStore.confirm({
    title: `Clear ${contentType}`,
    message: `Are you sure you want to clear all fields on ${contentType}?`,
    confirmText: "Clear",
    confirmColor: "error",
  });
  if (!confirmed) {
    return false;
  }

  let fields = [];
  let clearedExtraData = {};
  if (documentType === DOCUMENT_CLASSIFICATIONS.ORDER_FORM) {
    fields = [...orderFormFields.value.fields];
    // initializing with all extraData dict so that fields that are
    // not related to the section being cleared don't get overwritten
    clearedExtraData = props.orderForm.extraData;
  } else if (documentType === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD) {
    const ix = props.insuranceCards.findIndex((card) => card.id === item.id);
    fields = [...insuranceCardFields.value[ix].fields];
  } else if (documentType === DOCUMENT_CLASSIFICATIONS.TO_DO) {
    const ix = props.toDos.findIndex((toDo) => toDo.id === item.id);
    fields = [...toDoFields.value[ix].fields];
  }
  fields = fields.filter((field) => field.group === formGroup);

  const clearedFields = fields.reduce((acc, field) => {
    if (field.isExtraData) {
      clearedExtraData[field.key] = field.defaultValue;
    } else {
      acc[field.key] = field.defaultValue;
    }
    return acc;
  }, {});
  clearedFields.extraData = clearedExtraData;
  for (const key of Object.keys(clearedFields)) {
    emit("attributeEdit", { key }, clearedFields[key], documentType, item.id);
  }
  return true;
};

const onArchive = () => {
  emit("archive");
};

const onDeleteOrderFormGroup = async (orderForm, group) => {
  const hiddenGroups = orderForm.uiState?.hiddenGroups || [];
  if (hiddenGroups.includes(group)) {
    return;
  }
  const cleared = await onClearContent(orderForm, "ORDER_FORM", group);
  if (cleared) {
    hiddenGroups.push(group);
    emit("attributeEdit", { key: "uiState" }, { ...orderForm.uiState, hiddenGroups }, "ORDER_FORM", orderForm.id);
    revalidateOrderForm();
  }
};

const onAddOrderFormGroup = (orderForm, group) => {
  const hiddenGroups = orderForm.uiState?.hiddenGroups || [];
  if (!hiddenGroups.includes(group)) {
    return;
  }
  hiddenGroups.splice(hiddenGroups.indexOf(group), 1);
  emit("attributeEdit", { key: "uiState" }, { ...orderForm.uiState, hiddenGroups }, "ORDER_FORM", orderForm.id);
};

const isOrderFormGroupVisible = (orderForm, group) => {
  const hiddenGroups = orderForm.uiState?.hiddenGroups || [];
  return !hiddenGroups.includes(group);
};
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";

.preview-shell {
  background: $lh_color_snowy_white;
  border: 1px solid $lh_dash_border;
  border-radius: 16px;
}

// Mobile: show the extracted fields / checklist read-only and compact —
// non-interactive, edit buttons removed, smaller type and tighter spacing.
.document-sidebar--readonly {
  pointer-events: none;

  :deep(.v-btn) {
    display: none;
  }

  :deep(.v-field) {
    font-size: 13px;
  }

  :deep(.v-field__input) {
    min-height: 36px;
    padding-top: 4px;
    padding-bottom: 4px;
  }

  :deep(.v-label),
  :deep(.v-field-label) {
    font-size: 12px;
  }

  :deep(.v-input--density-default),
  :deep(.v-input) {
    --v-input-control-height: 40px;
    margin-bottom: 2px;
  }
}

.viewer-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  background: $lh_dash_surface;
  border: 1px solid $lh_dash_border;
  border-radius: 12px;
  padding: 2px 6px;
  width: fit-content;
  margin-left: auto;
  box-shadow: $lh_shadow_sm;

  .v-btn {
    color: $lh_color_navy;
  }

  .viewer-toolbar__spacer {
    width: 1px;
    height: 18px;
    background: $lh_dash_border;
    margin: 0 6px;
  }

  .viewer-toolbar__page {
    font-family: $lh_font_mono;
    font-size: 12.5px;
    font-weight: 500;
    color: $lh_color_navy;
    padding: 0 6px;
    white-space: nowrap;
  }
}

.snooze-btn {
  letter-spacing: normal;
  text-transform: none;
  font-weight: 600;
}

:deep(.intake-card) {
  border: 1px solid $lh_dash_border !important;
  border-radius: 14px !important;
  box-shadow: none !important;
  transition: border-color $lh_transition_medium, box-shadow $lh_transition_medium;

  .intake-card__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 16px 20px 0;
    font-family: $lh_font_family_dm_sans;

    &--stacked {
      flex-direction: column;
      align-items: stretch;
      gap: 2px;

      .intake-card__title-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
      }
    }
  }

  .intake-card__title-text {
    font-size: 15.5px;
    line-height: 22px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: $lh_color_navy_dark;
  }

  .intake-card__subtitle {
    font-size: 13.5px;
    line-height: 19px;
    color: $lh_color_blue_gray;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .intake-card__meta {
    font-size: 12.5px;
    line-height: 17px;
    color: $lh_color_blue_gray;
    opacity: 0.85;
  }

  .intake-card__alert {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 24px;
    padding: 0 10px;
    border-radius: 12px;
    background: rgba($lh_color_red, 0.08);
    color: $lh_color_red;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }

  .intake-card__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
  }

  &.intake-card--invalid {
    border-color: rgba($lh_color_red, 0.35) !important;
    box-shadow: 0 0 0 3px rgba($lh_color_red, 0.05) !important;
  }

  &.intake-card--selected {
    border-color: rgba($lh_color_green, 0.5) !important;
    box-shadow: 0 0 0 3px rgba($lh_color_green, 0.07) !important;
  }
}

.intake-add-content {
  background: $lh_dash_surface;
  border: 1px dashed rgba($lh_color_navy, 0.2);
  border-radius: 14px;
  padding: 18px 20px;

  .intake-add-content__hint {
    font-family: $lh_font_family_dm_sans;
    font-size: 13.5px;
    color: $lh_color_blue_gray;
    margin-bottom: 12px;
  }

  .intake-add-content__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .v-btn {
      letter-spacing: normal;
      text-transform: none;
    }
  }
}

.image-bounding-box-container {
  position: relative;
  display: inline-block;

  .document-preview-img {
    display: block;
    max-width: 100%;
    height: auto;
  }

  .highlight-overlay {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }
}

/** Forms and inputs still need to exist for validations to work, but need to be invisible */
/** "display: none" will not work for validations */
.invisible {
  visibility: hidden;
  height: 0;
  overflow: hidden;
  padding: 0;
  margin: 0;
}

// Below md the preview and sidebar columns stack, but the desktop viewer's
// JS-driven scroll/pan (absolute offset + fixed-height clip) keeps pushing the
// image down the page — right over the sidebar, eating its clicks. Whenever
// the columns are stacked, let the document flow normally and the page scroll.
@media (max-width: 959.98px) {
  .preview-image-scroll-container {
    position: static !important;
    top: 0 !important;
    height: auto !important;
    overflow: visible !important;
  }

  .preview-image-container {
    height: auto !important;
    overflow: visible !important;
  }
}

// Phones additionally lose the editing "add content" drop zone.
@media (max-width: 600px) {
  .intake-add-content {
    display: none !important;
  }
}
</style>
